/* Copyright (c) 2018 Backpack Health LLC */
import * as React from 'react';

import Card, { CardSection } from '../../../components/card/Card';
import { RenderSurveyAnswers } from '../SurveyQuestion/RenderSurveyQuestion';
import { iSurvey, iSurveyResponse, iSurveyTopLevelQuestion } from '../../../../apiServices/SurveyService';
import SurveyQuestionResponseModel from '../../../../models/SurveyQuestionResponseModel';
import { useSurveyDefinitionQuestionsForActiveProfile } from '../../../hooks/useSurvey';

interface Props {
  survey: iSurvey;
}

export const AnsweredSections = (props: Props) => {
  const { survey } = props;

  const [surveyDefQuestionData] = useSurveyDefinitionQuestionsForActiveProfile(survey.survey_definition_id);
  const questionSections = surveyDefQuestionData.sections;
  const responseLookup = survey.responses.reduce((r, surveyResponse) => {
    r[surveyResponse.question_id] = surveyResponse;
    return r;
  }, {});

  return (
    <>
    {
      questionSections.map((s, i) => (
        <SurveySection
          key={i}
          title={s.title}
          questions={s.questions}
          responseLookup={responseLookup}
        />
      ))
    }
    </>
  );
};

interface SurveySectionProps {
  title: string;
  questions: iSurveyTopLevelQuestion[];
  responseLookup: {
    [key: string]: iSurveyResponse;
  };
}

const AnsweredSectionQuestion = (props) => {
  return (
    <div className="font-footnote font-color-gsDark" style={{ marginBottom: 8 }}>
      {props.children}
    </div>
  );
};
const AnsweredSectionValue = (props) => {
  return (
    <div className="font-body font-color-gsDarkest">
      {props.children}
    </div>
  );
};

const SurveySection = (props: SurveySectionProps) => {
  const { title, questions, responseLookup } = props;

  const questionsNoMarkdown = questions.map(q => ({
    ...q,
    text: q.text.replace(/\*/g, ''),
  }));
  const questionsWithAnswers = questionsNoMarkdown.filter(q => Boolean(responseLookup[q.id]));
  const hasAnswers = Boolean(questionsWithAnswers.find((q) => Boolean(responseLookup[q.id])));
  if (!hasAnswers) { return null; }

  return (
    <div>
      <div className="font-footnote theme-group base color util-application-frame-forcards-padding" style={{ marginTop: 24, marginBottom: 8 }}>{title}</div>
      <Card>
        {
          questionsWithAnswers.map((q, i) => {
            const answer = responseLookup[q.id];
            const responseEvaluations = SurveyQuestionResponseModel.getResponseEvaluations(answer);

            return (
              <React.Fragment key={i}>
                <CardSection hasBorder>
                  <AnsweredSectionQuestion>{q.text}</AnsweredSectionQuestion>
                  <AnsweredSectionValue>
                    <RenderSurveyAnswers answer={answer} question={q} />
                  </AnsweredSectionValue>
                </CardSection>

                {
                  responseEvaluations.length > 0 ? (
                    <CardSection hasBorder>
                      {
                        responseEvaluations.map((e, ii) => {
                          const { id, value } = e;
                          const questionEvaluations = SurveyQuestionResponseModel.geQuestionEvaluations(q);
                          const q1 = questionEvaluations.find((qe) => qe.id === id);
                          const isLast = ii === responseEvaluations.length - 1;

                          return (
                            <div key={id} style={{ marginBottom: isLast ? 0 : 18 }}>
                              <AnsweredSectionQuestion>{q1.text}</AnsweredSectionQuestion>
                              <AnsweredSectionValue>
                                <span className="util-bold">
                                  {value}
                                </span>
                              </AnsweredSectionValue>
                            </div>
                          );
                        })
                      }
                    </CardSection>
                  ) : null
                }
              </React.Fragment>
            );
          })
        }
      </Card>
    </div>
  );
};
